// Phase 7: whether the thing that fills the feed is still working.
//
// DIRECTION: "A health strip on the Home screen, read from the run log, shows
// how many recipes are healthy, which are running on a fallback, which are
// broken and since when, and what the model spent on repairs this week. And an
// email arrives only when something needs a person."
//
// The strip is the quiet half of that pair. The alerter already interrupts
// when something needs attention; this is what you look at when you wonder
// whether the feed is thin because Berlin is quiet or because an adapter died
// on Tuesday. So it says nothing at all when everything is `ok` beyond one
// line, and grows only when it has something to report.
//
// N1 (modify): moved into Account under a collapsed "System" row by WP 2.3.
// Re-skinned onto the primitives by 2.5b — a `Card` holding a toggle `Row`
// and, once opened, one `Row` per fact. `.health*` is gone from `app.css`.

import { useState, type CSSProperties } from 'react'
import { Button, Card, Field, Row, Tag, type TagTone } from '../ui'
import { supabase } from '../lib/supabase'
import type { Alert, Recipe } from '../lib/types'
import { useIsAdmin, adminGate } from '../lib/useIsAdmin'

const BAD: Recipe['status'][] = ['broken', 'blocked', 'degraded']

/** a2-38: the leading slot is a status chip here, not a picture, and the
 *  row's default 56px cut "degraded" to "degrade". 76px holds "degraded"
 *  (71px measured), the same on every row so the names still line up; a
 *  two-word alert kind ("repair failed") breaks onto two lines in it. And
 *  every row wraps: the summary's last clause and a recipe's row count were
 *  the facts the ellipsis was eating. */
const ROW = { className: 'u-row--wrap', style: { '--u-lead': '76px' } as CSSProperties }

/** Worst first in the expanded list. `paused` sits last: it is a decision
 *  rather than a failure. */
const RANK: Recipe['status'][] = ['broken', 'blocked', 'degraded', 'ok', 'paused']

const STATUS_TONE: Record<string, TagTone> = {
  broken: 'bad', blocked: 'bad', degraded: 'warn', ok: 'ok', paused: 'neutral',
}

/** How long ago, in the shortest true form. A recipe that last ran four
 *  minutes ago and one that last ran in March are different kinds of fact and
 *  "2026-03-14T09:22:11Z" makes them look the same. */
function ago(iso: string | null): string {
  if (!iso) return 'never'
  const mins = Math.round((Date.now() - Date.parse(iso)) / 60000)
  if (mins < 2) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 36) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/** The last run of each recipe, for the expanded list. Keyed by recipe id;
 *  a recipe that has not run this week simply has no entry, which the row
 *  says rather than guessing at. */
export type LastRun = Map<string, { rows_valid: number | null; status: string | null; started_at: string }>

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined)?.replace(/\/+$/, '')

/** What each answer from `POST /invite` says, in the person's words. The
 *  Worker's 429 is Supabase's own mail limit (two an hour); nothing retries. */
const INVITE_WORDS: Record<number, string> = {
  400: 'That does not look like an email address.',
  403: 'Only an admin can invite.',
  409: 'That address already has an account.',
  429: 'Try again in an hour',
}

/**
 * U0.6: an admin types an address and Supabase mails an invite. Drawn by the
 * phone's System card and by the desk's Health screen, both admin-only; the
 * Worker checks `app_admins` again, so hiding this is not the lock.
 * The field clears when the invite is accepted, not before.
 */
export function InviteForm() {
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [sent, setSent] = useState<string | undefined>()

  async function submit() {
    const to = email.trim()
    if (!to || busy) return
    setBusy(true); setError(undefined); setSent(undefined)
    try {
      if (!WORKER_URL) throw new Error('VITE_WORKER_URL is not set')
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not signed in')
      const res = await fetch(`${WORKER_URL}/invite`, {
        method: 'POST',
        headers: { authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ email: to }),
      })
      if (res.ok) { setSent(`Invited ${to}.`); setEmail('') }
      else setError(INVITE_WORDS[res.status] ?? 'The invite did not go out.')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="stack" onSubmit={e => { e.preventDefault(); void submit() }}>
      <Field label="Invite by email" error={error} hint={sent}>
        <Field.Input type="email" value={email} autoComplete="off" onChange={e => setEmail(e.target.value)} />
      </Field>
      <div><Button type="submit" size="sm" variant="secondary" busy={busy} disabled={!email.trim()}>Invite</Button></div>
    </form>
  )
}

export function HealthStrip({
  recipes, alerts, spendUsd, wrongThisWeek, lastRun, onAcknowledge,
}: {
  recipes: Recipe[]
  /** Unacknowledged only. An acknowledged alert is history, not a state. */
  alerts: Alert[]
  /** What repair cost this week, across every recipe. */
  spendUsd: number
  lastRun: LastRun
  /** Cards Doug marked wrong this week, by recipe. DIRECTION's last phase 7
   *  bullet: "a nightly count of those per recipe feeds the health strip". */
  wrongThisWeek: Map<string, number>
  onAcknowledge: (id: string) => void
}) {
  // Above the guard, because a hook below an early return does not run on
  // the first render — the bug this codebase has paid for more than once.
  const [open, setOpen] = useState(false)
  // U0.3: acknowledging an alert is an admin write (U0.2). The strip is only
  // mounted for an admin today; this keeps "Seen" off it if that changes.
  const admin = adminGate(useIsAdmin()) === 'show'
  const canAck = admin

  if (!recipes.length) return null

  // The two `probe-` recipes are failure rigs from phase 1 — a dead URL and a
  // known 403, kept so the alerting path can be exercised on purpose. They
  // are paused and they are not adapters, and counting them made the strip
  // say "20 of 23 working · 3 paused", which Doug read as three broken ones.
  const adapters = recipes.filter(r => !r.id.startsWith('probe-'))

  const ok = adapters.filter(r => r.status === 'ok')
  const unwell = adapters.filter(r => BAD.includes(r.status))
  const paused = adapters.filter(r => r.status === 'paused')
  const wrong = [...wrongThisWeek.entries()].sort((a, b) => b[1] - a[1])

  // A recipe that is `ok` but has not run in three days is a cron that stopped,
  // and nothing else in the system notices: the alerter fires on a run going
  // wrong, not on a run never happening.
  const stale = ok.filter(r => !r.last_ok_at || Date.now() - Date.parse(r.last_ok_at) > 3 * 86400000)

  // Two questions that used to share one boolean: "is every adapter up?"
  // (the title) and "is there anything to look at?" (the tag). With alerts or
  // silent recipes but nothing broken, `healthy` was false, the title fell
  // into "N of M working" with N = M, and the tag said Issue beside it.
  const allUp = unwell.length === 0
  const quiet = allUp && alerts.length === 0 && stale.length === 0

  return (
    <Card>
      {/* Doug: "Let me click on the adapters to see which ones are up."
          Everything the list needs was already being fetched for this line;
          it was the line that was not expandable. */}
      <Row
        {...ROW}
        leading={<Tag tone={!allUp ? 'bad' : quiet ? 'ok' : 'warn'}>{!allUp ? 'Issue' : quiet ? 'OK' : 'Check'}</Tag>}
        title={allUp ? `All ${ok.length} adapters working` : `${ok.length} of ${ok.length + unwell.length} adapters working`}
        meta={[
          unwell.length > 0 ? `${unwell.length} broken` : null,
          stale.length > 0 ? `${stale.length} silent` : null,
          alerts.length > 0 ? `${alerts.length} alert${alerts.length === 1 ? '' : 's'}` : null,
          paused.length > 0 ? `${paused.length} paused on purpose` : null,
          spendUsd > 0 ? `$${spendUsd.toFixed(2)} on repairs this week` : 'no repair spend this week',
        ].filter(Boolean).join(' · ')}
        onPress={() => setOpen(v => !v)}
      />

      {/* Folded behind `open`, with their counts above so nothing is lost by
          collapsing — "1 broken · 16 silent" says what a tap would show. */}
      {open && [...adapters]
        // Worst first: the answer to "which ones are up" is really "which
        // ones aren't", and a list of 23 alphabetically buries the three.
        .sort((a, b) => RANK.indexOf(a.status) - RANK.indexOf(b.status) || a.name.localeCompare(b.name))
        .map(r => {
          const run = lastRun.get(r.id)
          return (
            <Row
              {...ROW}
              key={`all-${r.id}`}
              leading={<Tag tone={STATUS_TONE[r.status] ?? 'neutral'}>{r.status}</Tag>}
              title={r.name}
              // The three facts that answer "is it up": whether it is
              // working, when it last did, and what it last returned. A
              // recipe with no run this week says so — a blank would read
              // as zero rows, which is a different thing.
              meta={[
                r.status === 'paused' ? 'paused' : `last worked ${ago(r.last_ok_at)}`,
                run
                  ? `last run ${ago(run.started_at)}, ${run.rows_valid ?? 0} row${run.rows_valid === 1 ? '' : 's'}`
                  : 'no run in the last week',
              ].join(' · ')}
            />
          )
        })}

      {open && unwell.map(r => (
        <Row
          {...ROW}
          key={`unwell-${r.id}`}
          leading={<Tag tone={STATUS_TONE[r.status] ?? 'bad'}>{r.status}</Tag>}
          title={r.name}
          meta={`last worked ${ago(r.last_ok_at)}`}
        />
      ))}

      {open && stale.map(r => (
        <Row
          {...ROW}
          key={`stale-${r.id}`}
          leading={<Tag tone="warn">silent</Tag>}
          title={r.name}
          meta={`last ran ${ago(r.last_run_at)}`}
        />
      ))}

      {open && alerts.map(a => (
        <Row
          {...ROW}
          key={a.id}
          leading={<Tag tone="bad">{a.kind.replace(/_/g, ' ')}</Tag>}
          title={a.message}
          meta={null}
          trailing={canAck ? <Button variant="quiet" size="sm" onClick={() => onAcknowledge(a.id)}>Seen</Button> : undefined}
        />
      ))}

      {open && wrong.length > 0 && (
        <Row
          {...ROW}
          leading={<Tag tone="warn">wrong</Tag>}
          title={wrong.slice(0, 3).map(([recipe, n]) => `${recipe} ${n}`).join(' · ')}
          meta="cards you marked wrong this week"
        />
      )}

      {admin && (
        <div style={{ padding: 'var(--space-3) var(--space-4) var(--space-4)' }}><InviteForm /></div>
      )}
    </Card>
  )
}
